import { useCallback, useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import api from '../../api'
import Button from '../../components/Button'
import Select from '../../components/Select'
import { ReactComponent as DocumentIcon } from '../../assets/images/icons/ic-document-outlined.svg'
import HeaderFooterLayout from '../../components/layouts/HeaderFooterLayout'
import Pagination from '../../components/Pagination'
import typo from '../../typography.module.css'
import styles from './EventPartnerDashboard.module.css'

const vehicleOptions = [
  { id: 'any', name: 'Indifferente', value: '' },
  { id: 'yes', name: 'Sì', value: 'si' },
  { id: 'no', name: 'No', value: 'no' },
]

const contactFields = [
  ['firstname', 'Nome'], ['lastname', 'Cognome'], ['email', 'Email'], ['phone', 'Telefono'],
]

const fields = [
  ['city', 'Città', 'text', false, 150], ['radius_km', 'Raggio (km)', 'number'],
  ['experience_min', 'Anni di esperienza minimi', 'number'],
  ['experience_max', 'Anni di esperienza massimi', 'number'],
  ['years_since_experience', 'Anni dall’ultima esperienza', 'number'],
]

export default function EventPartnerDashboard({ user }) {
  const [data, setData] = useState(null)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState({ event_id: '', vehicle: '' })
  const requester = { firstname: user?.name || '', lastname: user?.surname || '', email: user?.email || '', phone: user?.phone || '' }
  const pending = useRef(null)
  const sendingRef = useRef(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await api.get(`/company/dashboard/event-partner?page=${page}&per=10`))
    } catch (_) {
      setError('Non è stato possibile caricare gli eventi e le richieste. Riprova.')
    } finally { setLoading(false) }
  }, [page])
  useEffect(() => { load() }, [load])

  const change = (key, value) => {
    setForm(previous => ({ ...previous, [key]: value }))
    setSuccess('')
  }
  const submit = async (event) => {
    event.preventDefault()
    if (sendingRef.current || loading || !data?.events?.length) return
    setError('')
    setSuccess('')
    if (!data.events.some(item => item.id === Number(form.event_id))) {
      setError('Seleziona un evento prima di inviare la richiesta.')
      return
    }
    const payload = { ...form, ...requester, event_id: Number(form.event_id), vehicle: form.vehicle || null, roles: form.roles || '', certificates: form.certificates || '', city: form.city || '' }
    for (const [key, , type] of fields) {
      if (type === 'number') payload[key] = form[key] === '' || form[key] == null ? null : Number(form[key])
    }
    if (payload.experience_min != null && payload.experience_max != null && payload.experience_min > payload.experience_max) {
      setError('L’esperienza minima non può superare quella massima.')
      return
    }
    const fingerprint = JSON.stringify(payload)
    if (!pending.current || pending.current.fingerprint !== fingerprint) pending.current = { fingerprint, key: uuidv4() }
    sendingRef.current = true
    setSending(true)
    try {
      await api.post('/company/talent/requests', { ...payload, request_key: pending.current.key })
      setSuccess('Richiesta ricevuta. Sarà trasmessa a iziwork, che ti contatterà per la ricerca dei profili.')
      await load()
    } catch (err) {
      setError(typeof err?.detail === 'string' ? err.detail : 'Invio non riuscito. Puoi riprovare senza duplicare la richiesta.')
    } finally {
      sendingRef.current = false
      setSending(false)
    }
  }

  const events = data?.events || []
  return <HeaderFooterLayout>
    <div className={styles.container}><div className={styles.section}><div className={styles.sectionInner}>
      <header className={styles.pageHeader}>
        <div className={styles.headingCopy}>
          <h1 className={typo.title}>Talent Village</h1>
          <p className={styles.subtitle}>Trova i profili per la tua azienda. Descrivi chi cerchi: iziwork gestirà la selezione e ti contatterà direttamente.</p>
        </div>
        <div className={styles.partnerBrand}>
          <img className={styles.partnerLogo} src="https://starting-finance-dev.fra1.digitaloceanspaces.com/medias/1799/pittogramma__20260805093722.webp" alt="iziwork" width="48" height="48" />
          <div><span>In collaborazione con</span><strong>iziwork</strong></div>
        </div>
      </header>
      {error && <div role="alert" className={styles.error}>{error}</div>}
      {success && <div role="status" className={styles.success}>{success}</div>}
      <form className={styles.requestCard} onSubmit={submit}>
        <div className={styles.formHeader}>
          <h2 className={styles.tableTitle}>Nuova richiesta</h2>
          <p className={styles.subtitle}>Indica i profili di cui hai bisogno. I campi con * sono obbligatori.</p>
        </div>
        <section className={styles.requesterSection} aria-labelledby="talent-requester-title">
          <h3 id="talent-requester-title" className={styles.sectionTitle}>Dati del richiedente</h3>
          <p className={styles.sectionDescription}>Questi dati provengono dal tuo account e saranno utilizzati da iziwork per contattarti.</p>
          <dl className={styles.requesterRecap}>
            <div className={styles.companyRecap}>
              <dt>Società</dt>
              <dd>
                {user?.company?.logo && <img className={styles.companyLogo} src={user.company.logo} alt={`Logo ${user.company.name || 'azienda'}`} />}
                <span>{user?.company?.name || 'Non disponibile'}</span>
              </dd>
            </div>
            {contactFields.map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{requester[key] || 'Non disponibile'}</dd></div>)}
          </dl>
        </section>
        <div className={styles.criteriaHeader}>
          <h3 id="talent-criteria-title" className={styles.sectionTitle}>Profili ricercati</h3>
          <p className={styles.sectionDescription}>Seleziona l’evento e descrivi le caratteristiche dei candidati che cerchi.</p>
        </div>
        <fieldset aria-labelledby="talent-criteria-title" disabled={sending || loading || !events.length} className={styles.formGrid}>
          <div className={styles.selectField} role="group" aria-labelledby="talent-event-label">
            <span id="talent-event-label">Evento *</span>
            <Select appearance="modern" portal placeholder="Seleziona un evento"
              options={events} selected={events.find(item => item.id === Number(form.event_id))}
              disabled={sending || loading || !events.length}
              onSelect={item => change('event_id', item?.id ?? '')} />
          </div>
          {fields.map(([key, label, type, required, maxLength]) => <label key={key}>
            {label}{required ? ' *' : ''}
            <input type={type} required={Boolean(required)} maxLength={maxLength} min={type === 'number' ? 0 : undefined}
              max={type === 'number' ? (key === 'radius_km' ? 20000 : 100) : undefined} step={type === 'number' ? 1 : undefined}
              value={form[key] ?? ''} onChange={e => change(key, e.target.value)} />
          </label>)}
          <div className={styles.selectField} role="group" aria-labelledby="talent-vehicle-label">
            <span id="talent-vehicle-label">Possiede un veicolo</span>
            <Select appearance="modern" portal options={vehicleOptions}
              selected={vehicleOptions.find(item => item.value === form.vehicle)}
              disabled={sending || loading || !events.length}
              onSelect={item => change('vehicle', item?.value ?? '')} />
          </div>
          <label className={styles.fullWidth}>Mansioni<textarea maxLength={2000} rows={3} value={form.roles || ''} onChange={e => change('roles', e.target.value)} /></label>
          <label className={styles.fullWidth}>Certificati<textarea maxLength={2000} rows={2} value={form.certificates || ''} onChange={e => change('certificates', e.target.value)} /></label>
        </fieldset>
        {!loading && !events.length && <p>Non ci sono eventi associati alla tua azienda. Contatta il team Starting Finance.</p>}
        <div className={styles.formActions}>
          <p className={styles.subtitle}>iziwork ti contatterà ai recapiti indicati nella richiesta.</p>
          <Button type="submit" additionalClass={styles.submitButton} disabled={sending || loading || !events.length}>{sending ? 'Invio in corso…' : 'Invia richiesta'}</Button>
        </div>
      </form>
      <section className={styles.requestCard} aria-busy={loading}>
        <h2 className={styles.tableTitle}>Le tue richieste</h2>
        <p className={styles.subtitle}>“Trasmessa” conferma l’invio a iziwork. La selezione dei profili viene gestita dall’agenzia fuori dalla piattaforma.</p>
        {!loading && data && !data.requests?.length && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon} aria-hidden="true"><DocumentIcon /></div>
            <h3>Nessuna richiesta inviata</h3>
            <p>Le tue ricerche di personale iniziano da qui. Compila il modulo qui sopra per raccontare a iziwork quali profili cerchi.</p>
            <span className={styles.emptyHint}>Dopo l’invio, potrai consultare qui i dettagli e lo stato delle tue richieste.</span>
          </div>
        )}
        <div className={styles.requestList}>
          {(data?.requests || []).map(request => <RequestItem key={request.id} request={request}
            eventName={events.find(event => event.id === request.event_id)?.name || `Evento ${request.event_id}`} />)}
        </div>
        {data?.count > 0 && <Pagination page={page} total={data.count} per={10} loading={loading} onChange={setPage} />}
      </section>
    </div></div></div>
  </HeaderFooterLayout>
}

function RequestItem({ request, eventName }) {
  const filters = request.filters || {}
  const transmitted = request.status === 'transmitted'
  const dateLabel = value => {
    if (!value) return 'Data non disponibile'
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? 'Data non disponibile' : date.toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }
  const experience = filters.experience_min != null && filters.experience_max != null
    ? `${filters.experience_min}–${filters.experience_max} anni`
    : filters.experience_min != null ? `Da ${filters.experience_min} anni`
    : filters.experience_max != null ? `Fino a ${filters.experience_max} anni` : 'Non specificata'
  const vehicle = filters.vehicle === 'si' ? 'Sì' : filters.vehicle === 'no' ? 'No' : 'Indifferente'
  const criteria = [
    ['Città', filters.city || 'Non specificata'],
    ['Raggio di ricerca', filters.radius_km != null ? `${filters.radius_km} km` : 'Non specificato'],
    ['Esperienza', experience],
    ['Anni dall’ultima esperienza', filters.years_since_experience ?? 'Non specificati'],
    ['Possiede un veicolo', vehicle],
    ['Certificati', filters.certificates || 'Non specificati'],
  ]
  return <article className={styles.requestItem}>
    <div className={styles.requestHeading}>
      <div className={styles.requestIdentity}>
        <span className={styles.requestReference}>Richiesta #{request.id} · {eventName}</span>
        <h3>{filters.roles || 'Ricerca di personale'}</h3>
      </div>
      <span className={`${styles.badge} ${transmitted ? styles.transmittedBadge : styles.receivedBadge}`}>
        <span aria-hidden="true">{transmitted ? '✓' : '◷'}</span>
        {transmitted ? 'Trasmessa a iziwork' : 'Ricevuta'}
      </span>
    </div>
    <dl className={styles.requestHighlights}>
      <div><dt>Località</dt><dd>{filters.city || 'Non specificata'}{filters.radius_km != null && <span> · raggio {filters.radius_km} km</span>}</dd></div>
      <div><dt>Esperienza richiesta</dt><dd>{experience}</dd></div>
      <div><dt>Richiesta inviata</dt><dd>{dateLabel(request.created_at)}</dd></div>
    </dl>
    <div className={`${styles.deliveryNote} ${transmitted ? styles.deliveredNote : ''}`}>
      <span className={styles.deliveryDot} aria-hidden="true" />
      <p>{transmitted
        ? <>Inviata a iziwork{request.transmitted_at ? ` il ${dateLabel(request.transmitted_at)}` : ''}. L’agenzia gestirà la selezione e ti contatterà ai recapiti indicati.</>
        : 'La richiesta è stata registrata. Qui potrai verificare quando sarà trasmessa a iziwork.'}</p>
    </div>
    <details className={styles.requestDetails}>
      <summary>Dettagli della richiesta<span aria-hidden="true" className={styles.detailsChevron}>⌄</span></summary>
      <div className={styles.detailSections}>
        <section>
          <h4>Profili ricercati</h4>
          <dl className={styles.detailGrid}>
            <div className={styles.fullWidth}><dt>Mansioni</dt><dd>{filters.roles || 'Non specificate'}</dd></div>
            {criteria.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
          </dl>
        </section>
        <section>
          <h4>Dati del richiedente</h4>
          <dl className={styles.detailGrid}>
            {contactFields.map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{filters[key] || 'Non disponibile'}</dd></div>)}
          </dl>
        </section>
      </div>
    </details>
  </article>
}
